/**
 * Draggable translucent panel. Y2K-ish: chrome-blue gradient tab, cyan glow,
 * rounded bezels, backdrop-blur body.
 *
 * The taskbar at the bottom of the screen is NOT a window — everything else
 * in the game UI (Log, Minimap, Focus/Compass, Side Panel, Dungeon info,
 * Debug) is built on this class so the player can rearrange the layout.
 */

export interface WindowPlacement {
  x: number;
  y: number;
  w?: number;
  h?: number;
}

export class GameWindow {
  readonly root: HTMLElement;
  readonly tab: HTMLElement;
  readonly body: HTMLElement;
  private drag: { dx: number; dy: number; pointerId: number } | null = null;
  private static zCounter = 40;

  constructor(title: string, id: string) {
    this.root = document.createElement('div');
    this.root.className = 'gw';
    this.root.dataset.gwid = id;
    this.root.dataset.inspect = title; // cursor hover-inspect name (` debug)
    this.root.innerHTML = `
      <div class="gw-tab">
        <span class="gw-tab-notch">◈</span>
        <span class="gw-tab-title">${escapeHtml(title)}</span>
        <span class="gw-tab-notch">◈</span>
      </div>
      <div class="gw-body"></div>
    `;
    this.tab = this.root.querySelector('.gw-tab') as HTMLElement;
    this.body = this.root.querySelector('.gw-body') as HTMLElement;
    this.wireDrag();
    // Clicking anywhere in the window brings it to the front.
    this.root.addEventListener('pointerdown', () => this.raise(), true);
  }

  place(p: WindowPlacement): this {
    this.root.style.left = `${p.x}px`;
    this.root.style.top = `${p.y}px`;
    if (p.w !== undefined) this.root.style.width = `${p.w}px`;
    if (p.h !== undefined) this.root.style.height = `${p.h}px`;
    return this;
  }

  mount(parent: HTMLElement): this {
    parent.appendChild(this.root);
    return this;
  }

  raise(): void {
    this.root.style.zIndex = String(++GameWindow.zCounter);
  }

  setContent(html: string): void {
    this.body.innerHTML = html;
  }

  private wireDrag(): void {
    this.tab.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.raise();
      this.drag = {
        dx: e.clientX - this.root.offsetLeft,
        dy: e.clientY - this.root.offsetTop,
        pointerId: e.pointerId,
      };
      this.tab.setPointerCapture(e.pointerId);
    });
    this.tab.addEventListener('pointermove', (e) => {
      if (!this.drag || this.drag.pointerId !== e.pointerId) return;
      const x = e.clientX - this.drag.dx;
      const y = e.clientY - this.drag.dy;
      // Keep the tab reachable — clamp to the visible viewport with a margin.
      const maxX = window.innerWidth - 40;
      const maxY = window.innerHeight - 20;
      this.root.style.left = `${Math.max(-this.root.offsetWidth + 40, Math.min(x, maxX))}px`;
      this.root.style.top = `${Math.max(0, Math.min(y, maxY))}px`;
    });
    const end = (e: PointerEvent) => {
      if (this.drag && this.drag.pointerId === e.pointerId) this.drag = null;
    };
    this.tab.addEventListener('pointerup', end);
    this.tab.addEventListener('pointercancel', end);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}
